import React, { useState, useEffect, useCallback } from 'react';
import VacationsService from '@services/VacationsService';
import './PTOPermissionsPage.css';
import { Modal } from '@/components/utils/Modal';

interface PermissionItem {
  id: number;
  name: string;
  days: number;
  message: string;
  isWorkingDay: boolean;
  availableEndDate: boolean;
  isHalfDay: boolean;
  isHRApproverAllowed: boolean;
  isPTOApproverAllowed: boolean;
  canSelectPastDates: boolean;
}

type ModalOption = 'add' | 'edit' | 'delete';

interface ModalForm {
  name: string;
  days: number;
  message: string;
  isWorkingDay: boolean;
  availableEndDate: boolean;
  isHalfDay: boolean;
  isHRApproverAllowed: boolean;
  isPTOApproverAllowed: boolean;
  canSelectPastDates: boolean;
}

const EMPTY_FORM: ModalForm = {
  name: '',
  days: 0,
  message: '',
  isWorkingDay: false,
  availableEndDate: false,
  isHalfDay: false,
  isHRApproverAllowed: false,
  isPTOApproverAllowed: false,
  canSelectPastDates: false,
};

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const CheckboxField: React.FC<CheckboxFieldProps> = ({ label, checked, onChange }) => (
  <div className="permission-input-modal">
    <div className="corgee-layout-table-cell corgee-label">{label}</div>
    <div className="corgee-layout-table-cell">
      <input
        className="corgee-input"
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.currentTarget.checked)}
      />
    </div>
  </div>
);

const BoolCell: React.FC<{ value: boolean }> = ({ value }) => (
  <td className="corgee-grid-cell stime-td-report">{value ? 'yes' : 'no'}</td>
);

const PTOPermissions: React.FC = () =>
{
  const background = `${'./src/assets/icons/add-new-button.svg'}`;

  const [itemsFillTable, setItemsFillTable] = useState<PermissionItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedOption, setSelectedOption] = useState<ModalOption | null>(null);
  const [selectedItem, setSelectedItem] = useState<PermissionItem | null>(null);
  const [form, setForm] = useState<ModalForm>(EMPTY_FORM);
  const [invalidMessage, setInvalidMessage] = useState('');

  const fillTable = useCallback(() =>
  {
    VacationsService.GetPTOPermissions().then(
      (
        data: Array<{
          Id: number;
          Name: string;
          Days: number;
          Message: string;
          IsWorkingDay: boolean;
          AvailableEndDate: boolean;
          IsHalfDay: boolean;
          IsHRApproverAllowed: boolean;
          IsPTOApproverAllowed: boolean;
          CanSelectPastDates: boolean;
        }>,
      ) =>
      {
        setItemsFillTable(
          data.map(d => ({
            id: d.Id,
            name: d.Name,
            days: d.Days,
            message: d.Message,
            isWorkingDay: d.IsWorkingDay,
            availableEndDate: d.AvailableEndDate,
            isHalfDay: d.IsHalfDay,
            isHRApproverAllowed: d.IsHRApproverAllowed,
            isPTOApproverAllowed: d.IsPTOApproverAllowed,
            canSelectPastDates: d.CanSelectPastDates,
          })),
        );
      },
    );
  }, []);

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    fillTable();
  }, [fillTable]);

  const openModal = useCallback((item: PermissionItem | null, option: ModalOption) =>
  {
    const titles: Record<ModalOption, string> = {
      add: 'Add New Permission',
      edit: 'Edit Permission',
      delete: 'Delete Permission',
    };
    setSelectedItem(item);
    setSelectedOption(option);
    setModalTitle(titles[option]);
    setForm(
      item
        ? {
          name: item.name,
          days: item.days,
          message: item.message,
          isWorkingDay: item.isWorkingDay,
          availableEndDate: item.availableEndDate,
          isHalfDay: item.isHalfDay,
          isHRApproverAllowed: item.isHRApproverAllowed ?? false,
          isPTOApproverAllowed: item.isPTOApproverAllowed ?? false,
          canSelectPastDates: item.canSelectPastDates ?? false,
        }
        : EMPTY_FORM,
    );
    setInvalidMessage('');
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() =>
  {
    setShowModal(false);
    setSelectedItem(null);
    setSelectedOption(null);
    setInvalidMessage('');
  }, []);

  const onSaveItemChanges = useCallback(() =>
  {
    const isDelete = selectedOption === 'delete';
    const nameValid = form.name.trim().length > 0;
    const daysValid = form.days >= 1;

    if (!isDelete && (!nameValid || !daysValid))
    {
      setInvalidMessage(!nameValid ? 'Invalid Permission Name' : 'Invalid Number of Days');
      return;
    }

    const requestModel = {
      PTOPermissionId: selectedItem?.id ?? null,
      Name: form.name,
      Days: form.days,
      Message: form.message ?? '',
      IsWorkingDay: form.isWorkingDay,
      AvailableEndDate: form.availableEndDate,
      IsHalfDay: form.isHalfDay,
      IsHRApproverAllowed: form.isHRApproverAllowed,
      IsPTOApproverAllowed: form.isPTOApproverAllowed,
      CanSelectPastDates: form.canSelectPastDates,
      Action: selectedOption,
    };

    VacationsService.UpdatePTOPermissions(requestModel).then(() =>
    {
      fillTable();
      closeModal();
    });
  }, [selectedOption, selectedItem, form, fillTable, closeModal]);

  return (
    <div
      id="PTOPermissions"
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <div className="PTOPermissions" style={{ height: 'auto' }}>
        <div className="react-corgee-wizard-securiry-groups-container react-corgee-wizard_max-width-1180">
          <div className="col-md-12">
            <h3 className="react-corgee-wizard-section-container_section-title">PTO Permissions</h3>
          </div>

          <div className="react-corgee-wizard-section-container_section-bottom">
            {/* Add button */}
            <div className="new-permission-button">
              <div className="react-corgee-wizard_add-new-option-div">
                <a
                  href="#"
                  className="react-corgee-wizard_add-new-option"
                  onClick={e =>
                  {
                    e.preventDefault();
                    openModal(null, 'add');
                  }}
                >
                  <span
                    className="react-corgee-wizard_anb-icon"
                    style={{ backgroundImage: `url(${background})` }}
                  />
                  <span>Add New Permission</span>
                </a>
              </div>
            </div>

            {/* Table */}
            <div id="tableContainer" className="table-responsive wrapper positions-view">
              <table className="corgee-grid">
                <thead className="corgee-grid-header">
                  <tr>
                    {[
                      'Name',
                      'Days',
                      'Alert Message',
                      'Is Working day?',
                      'Available End Date?',
                      'Is Half Day?',
                      'HR Approver allowed?',
                      'PTO Approver allowed?',
                      'Can select past dates?',
                      'Actions',
                    ].map(label => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {itemsFillTable.length > 0 ? (
                    itemsFillTable.map(item => (
                      <tr key={item.id}>
                        <td className="corgee-grid-cell stime-td-report">{item.name}</td>
                        <td className="corgee-grid-cell stime-td-report">{item.days}</td>
                        <td
                          className="corgee-grid-cell stime-td-report ellipsis"
                          title={item.message}
                        >
                          {item.message}
                        </td>
                        <BoolCell value={item.isWorkingDay} />
                        <BoolCell value={item.availableEndDate} />
                        <BoolCell value={item.isHalfDay} />
                        <BoolCell value={item.isHRApproverAllowed} />
                        <BoolCell value={item.isPTOApproverAllowed} />
                        <BoolCell value={item.canSelectPastDates} />
                        <td className="corgee-grid-cell stime-td-report">
                          <button
                            className="corgee-button edit"
                            onClick={() => openModal(item, 'edit')}
                          >
                            Edit
                          </button>
                          <button
                            className="corgee-button delete"
                            onClick={() => openModal(item, 'delete')}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ border: 'none' }}>
                        <div className="row spaceRow" />
                        <div className="row spaceRow" />
                        <div className="row spaceRow">
                          <div className="col-md-12">
                            <div className="stime-bottom-Results notFoundRecords">
                              No results found
                            </div>
                          </div>
                        </div>
                        <div className="row spaceRow" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal */}
        <Modal open={showModal} onClose={closeModal} title={modalTitle} preventCloseClickOut>
          <div
            className="md-transition-in"
            role="dialog"
            tabIndex={-1}
            style={{ opacity: 1, position: 'static' }}
          >
            <form>
              <div style={{ display: 'table' }} id="dialog_23">
                <div className="md-dialog-content delete-item-modal">
                  {selectedOption === 'delete' ? (
                    <div>{`Are you sure you want to remove Corgee's ${form.name} PTO Permission?`}</div>
                  ) : (
                    <div className="new-security-group positions-view">
                      <div className="corgee-layout-table">
                        {/* Text fields */}
                        <div className="columns-grid-2">
                          <div className="permission-input-modal">
                            <div className="corgee-layout-table-cell corgee-label">
                              Permission Name:
                            </div>
                            <div className="corgee-layout-table-cell">
                              <input
                                className="corgee-input"
                                type="text"
                                value={form.name}
                                onChange={e =>
                                {
                                  const value = e.currentTarget.value;
                                  setForm(f => ({ ...f, name: value }));
                                }}
                              />
                            </div>
                          </div>
                          <div className="permission-input-modal">
                            <div className="corgee-layout-table-cell corgee-label">
                              Available Days:
                            </div>
                            <div className="corgee-layout-table-cell">
                              <input
                                className="corgee-input"
                                type="number"
                                step={1}
                                value={form.days}
                                onChange={e =>
                                {
                                  const value = e.currentTarget.value;
                                  setForm(f => ({ ...f, days: Number(value) }));
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="permission-input-modal">
                          <div className="corgee-layout-table-cell corgee-label">Message:</div>
                          <div className="corgee-layout-table-cell">
                            <input
                              className="corgee-input"
                              type="text"
                              value={form.message}
                              onChange={e =>
                              {
                                const value = e.currentTarget.value;
                                setForm(f => ({ ...f, message: value }));
                              }}
                            />
                          </div>
                        </div>

                        {/* Checkboxes */}
                        <div className="columns-grid-4">
                          <CheckboxField
                            label="Is Working Day?:"
                            checked={form.isWorkingDay}
                            onChange={v => setForm(f => ({ ...f, isWorkingDay: v }))}
                          />
                          <CheckboxField
                            label="Available End Date?:"
                            checked={form.availableEndDate}
                            onChange={v => setForm(f => ({ ...f, availableEndDate: v }))}
                          />
                          <CheckboxField
                            label="Is Half Day?:"
                            checked={form.isHalfDay}
                            onChange={v => setForm(f => ({ ...f, isHalfDay: v }))}
                          />
                          <CheckboxField
                            label="HR Approver allowed?:"
                            checked={form.isHRApproverAllowed}
                            onChange={v => setForm(f => ({ ...f, isHRApproverAllowed: v }))}
                          />
                          <CheckboxField
                            label="PTO Approver allowed?:"
                            checked={form.isPTOApproverAllowed}
                            onChange={v => setForm(f => ({ ...f, isPTOApproverAllowed: v }))}
                          />
                          <CheckboxField
                            label="Can select past dates?:"
                            checked={form.canSelectPastDates}
                            onChange={v => setForm(f => ({ ...f, canSelectPastDates: v }))}
                          />
                        </div>
                      </div>

                      {invalidMessage.trim().length > 0 && (
                        <div className="cw-validation-message">{invalidMessage}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
                <button
                  type="button"
                  onClick={onSaveItemChanges}
                  className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
                >
                  Cancel
                </button>
              </div>
            </form>
            <div className="md-dialog-focus-trap" tabIndex={0} />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default PTOPermissions;
